import { get } from 'lodash'
import { flow, types } from 'mobx-state-tree'
import { deleteUser } from '../../network/deleteUser'
import { getAllUsers } from '../../network/getAllUsers'
import { getGroupIds } from '../../network/getGroupIds'
import { getUserWithId } from '../../network/getUserWithId'
import { userUpdate } from '../../network/userUpdate'
import { MessageTypes, getErrorMessage } from '../../utils'
import { RootStore } from '../store'
import { User } from './User'

const Group = types.model('Group', {
  id: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
})

const getDefaultUserConfigs = (user: any) => {
  const config = user?.accessConfig // && JSON.parse(user?.accessConfig)
  const keys = Object.keys(config)
  const retArr: any = []
  keys.map((key: any) => {
    if (key !== 'access_management') {
      const subTitles = Object.keys(config[key])
      const subTitlesArray: any = []
      subTitles.map((subTitle: any) => {
        if (subTitle !== 'isAccessible') {
          const subTitleData = {
            title: subTitle,
            titleAccessible: false,
            fetch: false,
            add: false,
            approve: false,
            delete: false,
            update: false,
          }
          subTitlesArray.push(subTitleData)
        }
      })
      const data = {
        title: key,
        titleAccessible: false,
        subTitleData: subTitlesArray,
      }
      retArr.push(data)
    }
  })

  return { ...user, name: '', id: undefined, accessConfig: retArr }
}

export const UserManagement = types
  .model('UserManagement', {
    users: types.array(User),
    groups: types.array(Group),
    selectedSection: types.optional(types.string, ''),
  })
  .actions((self) => ({
    fetchAllUsers: flow(function* fetchAllUsers(data: any, paginationInfo: any) {
      try {
        const groupIdResponse = yield getGroupIds(RootStore.session.getTokens)
        self.groups = groupIdResponse
        const response = yield getAllUsers(data, paginationInfo)

        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchGroupId: flow(function* fetchGroupId() {
      if (self.groups?.length > 0) {
        return
      }
      try {
        const response = yield getGroupIds(RootStore.session.getTokens)
        self.groups = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateUser: flow(function* updateUser(data: any) {
      try {
        const response = yield userUpdate(data, self.groups, RootStore.session.getTokens)
        RootStore.message.setMessage('User Update Success', MessageTypes.SUCCESS)
        return response 
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    userDelete: flow(function* userDelete(id) {
      try {
        const response = yield deleteUser(id, RootStore.session.getTokens)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchUserDetails: flow(function* fetchUserDetails(id: any) {
      try {
        const groupIdResponse = yield getGroupIds(RootStore.session.getTokens)
        self.groups = groupIdResponse
        let response: any
        if (id) {
          const response = yield getUserWithId(id, RootStore.session.getTokens)
          if (self?.users?.length === 0) {
            self.users.push(JSON.parse(JSON.stringify(response)))
          } else {
            const users: any = self?.users?.map((item: any) => {
              if (item?.id === response.id) {
                return response
              } else {
                return item
              }
            })
            self.users = users
          }
        }
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateSelectedSection(data: any) {
      self.selectedSection = data.isExpand ? '' : data.title
    },
  }))
  .views((self) => ({
    getUsers(listData: any, keys?: any, searchText?: any) {
      const users = listData ?? self?.users ?? []
      const getGroupTypeFromId = (id: any) => {
        return self?.groups?.filter((item: any) => item.id === id)?.[0]?.value ?? id
      }
      const data = users?.map((item: any) => {
        return {
          ID: item?.id,
          name: item?.name ?? '',
          type: item?.type ?? 'NA',
          group: getGroupTypeFromId(item?.group),
          lastLogin: item?.lastLogin ?? '',
          createdBy: item?.createdBy ?? '',
          createdDate: item?.createdDate ?? '',
          status: item?.status ?? 'Active',
          id: item?.id,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return users?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },

    getUserDetails(id: any): any {
      const user = self?.users?.filter((item: any) => item?.id.toString() === id?.toString())?.[0]
      let config = user?.accessConfig && JSON.parse(user?.accessConfig)
      const retArr: any = []
      if (!config) {
        const config = getDefaultUserConfigs(RootStore.user)
        const groupNames: any = self.groups
        const groupName = groupNames?.filter((item: any) => item?.id === user?.group)?.[0]?.value
        return { ...user, groupName: groupName ?? '', accessConfig: config?.accessConfig }
      } else {
        const keys = Object.keys(config)
        keys.map((key: any) => {
          const subTitles = Object.keys(config[key])
          const subTitlesArray: any = []
          subTitles.map((subTitle: any) => {
            if (subTitle !== 'isAccessible') {
              const subTitleData = {
                title: subTitle,
                titleAccessible: get(config, [key, subTitle, 'isAccessible'], null),
                fetch: get(config, [key, subTitle, 'fetch', 'isAccessible'], null),
                add:
                  get(config, [key, subTitle, 'add', 'isAccessible'], null) ||
                  get(config, [key, subTitle, 'create', 'isAccessible'], null),
                approve: get(config, [key, subTitle, 'approve', 'isAccessible'], null),
                delete: get(config, [key, subTitle, 'delete', 'isAccessible'], null),
                update: get(config, [key, subTitle, 'update', 'isAccessible'], null),
                unlink: get(config, [key, subTitle, 'unlink', 'isAccessible'], null),
              }
              subTitlesArray.push(subTitleData)
            }
          })
          const data = {
            title: key,
            titleAccessible: get(config, [key, 'isAccessible'], null),
            subTitleData: subTitlesArray,
          }
          retArr.push(data)
        })

        const groupNames: any = self.groups
        const groupName = groupNames?.filter((item: any) => item?.id === user?.group)?.[0]?.value
        return { ...user, groupName: groupName ?? '', accessConfig: retArr }
      }
    },
    getGroupIdNames() {
      return self.groups
    },
  }))

export const userManagementdata = UserManagement.create({
  users: [],
})
